/**
 * Locating the ONE word inside a display headline that carries a drawn accent — the pencil stroke under
 * "wait" / "așteaptă" on the advertising hero.
 *
 * Plain TypeScript that imports NO React, because it is a rule about copy rather than about markup: the
 * catalog holds each locale's headline as a WHOLE SENTENCE plus the word to mark, and this decides where
 * that word sits in it. Splitting it in the copy module instead would make every locale author three
 * fragments in the app's word order — the concatenation trap the repo's i18n convention exists to stop —
 * and splitting it inside the component would bury a rule with three real edge cases in a render path
 * that has no tests.
 *
 * The rule has a defined answer for every input, and the answer is never "throw away part of the
 * headline": a reworded translation whose accent word no longer appears renders plainly, with the whole
 * sentence intact. The stroke is decoration; the sentence is the content.
 */

/** A headline cut into the run before the accented word, that word, and the run after it. */
export interface AccentedHeadline {
	/** Everything before the accented word — `""` when the word opens the headline. */
	readonly lead: string;
	/** The accented word itself, or `""` when there is nothing to accent. */
	readonly accent: string;
	/** Everything after the accented word — `""` when the word closes the headline. */
	readonly trail: string;
}

/**
 * Split `headline` around the first WHOLE-WORD occurrence of `accent`.
 *
 * Whole-word rather than a bare `indexOf`, because the accent words in play are short and common: "wait"
 * is inside "waiting", so a first-occurrence search would draw the stroke under the first four letters of
 * the wrong word and leave "ing" hanging past its end.
 *
 * A HYPHEN is a boundary, not a joiner — "wait" in "wait-and-see" is the standalone word by this rule.
 * That is deliberate and pinned by a test; do not read the rule as "skips anything with punctuation
 * attached".
 *
 * @param headline the complete sentence, exactly as the locale's catalog holds it
 * @param accent the word within it to mark; an absent or empty word yields no accent
 */
export function splitAccentedHeadline(headline: string, accent: string): AccentedHeadline {
	if (accent.length === 0) {
		return {lead: headline, accent: "", trail: ""};
	}
	for (let at = headline.indexOf(accent); at !== -1; at = headline.indexOf(accent, at + 1)) {
		if (standsAlone(headline, at, accent.length)) {
			return {lead: headline.slice(0, at), accent, trail: headline.slice(at + accent.length)};
		}
	}
	return {lead: headline, accent: "", trail: ""};
}

/** Whether the run at `at` is bounded by non-letters on both sides — i.e. is a word rather than a part. */
function standsAlone(headline: string, at: number, length: number): boolean {
	return !isLetter(headline[at - 1]) && !isLetter(headline[at + length]);
}

/**
 * Unicode letter test, deliberately not the regex `\b`.
 *
 * JavaScript's word boundary is defined over `[A-Za-z0-9_]`, so it reads every Romanian diacritic as a
 * NON-letter — which means `\b` puts a boundary in the middle of a Romanian word. A word ending "ă"
 * followed by the accent word, or the accent word followed by one starting "ș", both look like separate
 * words to it. `\p{L}` is the actual question being asked, and the test named for this line fails under
 * an ASCII-only class rather than merely passing under both.
 */
function isLetter(character: string | undefined): boolean {
	return character !== undefined && /\p{L}/u.test(character);
}
